<template>
  <section class="section container">
    <div class="container mt-0 mb-6">
      <h1 class="title is-4 mb-3">
        {{ $t("factoring module settings") }}
      </h1>
    </div>

    <generic-tabs
      orientation="vertical"
      :tabs="[
        { name: 'tab1', label: $t('general settings') },
        { name: 'tab2', label: $t('bulk process') },
        { name: 'tab3', label: $t('wealth status source') },
        { name: 'tab4', label: $t('electronic signature source') },
        { name: 'tab5', label: $t('sirc source') },
        { name: 'tab6', label: $t('scripts') },
        { name: 'tab7', label: $t('debt level data source') },
      ]"
    >
      <template v-slot:tab1>
        <div class="content">
          <h2 class="title is-5">{{ $t("general settings") }}</h2>
          <div class="title is-6 mt-6">{{ $t("storage server") }}</div>
          <div class="fixed-grid has-3-cols">
            <div class="grid">
              <div class="cell">
                <div class="field">
                  <label class="label">{{ $t("shared folder path") }}</label>
                  <div class="control has-icons-left has-icons-right">
                    <input
                      class="input"
                      type="text"
                      v-model="meta.shared_folder_path"
                    />
                    <span class="icon is-small is-left">
                      <i class="fa fa-server"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab2>
        <div class="content">
          <h2 class="title is-5">{{ $t("bulk process") }}</h2>
          <div class="field">
            <label class="label">{{ $t("sat bulk process chunk size") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.sat_bulk_process_chunk_size"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-cube"></i>
              </span>
            </div>
          </div>
          <div class="field">
            <label class="label">{{ $t("sib bulk process chunk size") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.sib_bulk_process_chunk_size"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-cube"></i>
              </span>
            </div>
          </div>
          <div class="field">
            <label class="label">{{
              $t("wealth status bulk process chunk size")
            }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.wealth_status_bulk_process_chunk_size"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-cube"></i>
              </span>
            </div>
          </div>
          <div class="field">
            <label class="label">{{
              $t("sirc bulk process chunk size")
            }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.sirc_bulk_process_chunk_size"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-cube"></i>
              </span>
            </div>
          </div>
          <div class="field">
            <label class="label">{{ $t("error reporting api url") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.error_report_api_url"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-server"></i>
              </span>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab3>
        <div class="content">
          <h2 class="title is-5">{{ $t("wealth status source") }}</h2>
          <div class="columns mt-5">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("server") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.wealth_status_datasource_server"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("database") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.wealth_status_datasource_database"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-database"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("username") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.wealth_status_datasource_username"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("password") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="password"
                    v-model="meta.wealth_status_datasource_password"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-key"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-3">
              <label class="label">{{ $t("test connection") }}</label>
              <div
                class="button is-rounded is-primary"
                @click="test_connection"
              >
                <span class="icon"><i class="fa fa-bolt"></i></span>
                <span>{{ $t("test") }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab4>
        <div class="content">
          <h2 class="title is-5">{{ $t("electronic signature source") }}</h2>
          <div class="columns mt-5">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("server") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.electronic_signature_datasource_server"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("database") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.electronic_signature_datasource_database"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-database"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("username") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.electronic_signature_datasource_username"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("password") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="password"
                    v-model="meta.electronic_signature_datasource_password"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-key"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-3">
              <label class="label">{{ $t("test connection") }}</label>
              <div
                class="button is-rounded is-primary"
                @click="test_fe_connection"
              >
                <span class="icon"><i class="fa fa-bolt"></i></span>
                <span>{{ $t("test") }}</span>
              </div>
            </div>
          </div>

          <h1 class="title is-5 mt-5">
            {{ $t("electronic signature server to copy image from") }}
          </h1>
          <div class="columns mt-5">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("server") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.electronic_signature_copy_image_server"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("username") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.electronic_signature_copy_image_username"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("password") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="password"
                    v-model="meta.electronic_signature_copy_image_password"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-key"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-3">
              <label class="label">{{ $t("test connection") }}</label>
              <div
                class="button is-rounded is-primary"
                @click="test_copy_image_connection"
              >
                <span class="icon"><i class="fa fa-bolt"></i></span>
                <span>{{ $t("test") }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab5>
        <div class="content">
          <h2 class="title is-5">{{ $t("sirc source") }}</h2>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{
                  $t("api url for getting sirc users")
                }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.sirc_users_api_url"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{
                  $t("api url for getting sirc report")
                }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.sirc_report_api_url"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column" v-if="meta.sirc_users_api_url">
              <generic-dropdown
                :title="$t('sirc profile')"
                :placeholder="$t('select one')"
                :is_multiple="false"
                :url="meta.sirc_users_api_url"
                name_field="name"
                value_field="profile_id"
                @selected="sirc_profile_changed"
                ref="sirc_profile_dropdown"
              />
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("sirc api username") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.sirc_api_username"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("sirc api password") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="password"
                    v-model="meta.sirc_api_password"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-key"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <generic-dropdown
                :title="$t('drive for storing pdfs')"
                :placeholder="$t('select one')"
                :is_multiple="false"
                url="/drive/catalog"
                name_field="name"
                value_field="drive_id"
                @selected="sirc_drive_changed"
                ref="sirc_drive_dropdown"
              />
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab6>
        <div class="content">
          <h2 class="title is-5">{{ $t("scripts") }}</h2>
          <div class="fixed-grid has-4-cols">
            <div class="grid">
              <div class="cell">
                <div class="title is-6">{{ $t("sat rtu (data)") }}</div>
              </div>
              <div class="cell is-col-span-3">
                <div class="columns">
                  <div class="column is-4">
                    <generic-dropdown
                      :title="$t('sat rtu (data)')"
                      :placeholder="$t('select one')"
                      :is_multiple="false"
                      url="/script/catalog"
                      name_field="name"
                      value_field="directory"
                      @selected="sat_script_changed"
                      ref="sat_script_dropdown"
                    />
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">{{
                        $t("sat report data api url")
                      }}</label>
                      <div class="control has-icons-left has-icons-right">
                        <input
                          class="input"
                          type="text"
                          v-model="meta.sat_report_data_api_url"
                        />
                        <span class="icon is-small is-left">
                          <i class="fa fa-server"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cell">
                <div class="title is-6">{{ $t("sat rtu (file)") }}</div>
              </div>
              <div class="cell is-col-span-3">
                <div class="columns">
                  <div class="column is-4">
                    <generic-dropdown
                      :title="$t('sat rtu (file)')"
                      :placeholder="$t('select one')"
                      :is_multiple="false"
                      url="/script/catalog"
                      name_field="name"
                      value_field="directory"
                      @selected="sat_file_script_changed"
                      ref="sat_file_script_dropdown"
                    />
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">{{
                        $t("sat report file api url")
                      }}</label>
                      <div class="control has-icons-left has-icons-right">
                        <input
                          class="input"
                          type="text"
                          v-model="meta.sat_report_file_api_url"
                        />
                        <span class="icon is-small is-left">
                          <i class="fa fa-server"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cell">
                <div class="title is-6">{{ $t("sat nit search") }}</div>
              </div>
              <div class="cell is-col-span-3">
                <div class="columns">
                  <div class="column is-4">
                    <generic-dropdown
                      :title="$t('sat nit search')"
                      :placeholder="$t('select one')"
                      :is_multiple="false"
                      url="/script/catalog"
                      name_field="name"
                      value_field="directory"
                      @selected="sat_search_script_changed"
                      ref="sat_search_script_dropdown"
                    />
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">{{
                        $t("sat search report api url")
                      }}</label>
                      <div class="control has-icons-left has-icons-right">
                        <input
                          class="input"
                          type="text"
                          v-model="meta.sat_search_report_api_url"
                        />
                        <span class="icon is-small is-left">
                          <i class="fa fa-server"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cell">
                <div class="title is-6">{{ $t("wealth status") }}</div>
              </div>
              <div class="cell is-col-span-3">
                <div class="columns">
                  <div class="column is-4">
                    <generic-dropdown
                      :title="$t('wealth status script')"
                      :placeholder="$t('select one')"
                      :is_multiple="false"
                      url="/script/catalog"
                      name_field="name"
                      value_field="directory"
                      @selected="wealth_status_script_changed"
                      ref="wealth_status_script_dropdown"
                    />
                  </div>
                  <div class="column">
                    <div class="field">
                      <label class="label">{{
                        $t("wealth status report api url")
                      }}</label>
                      <div class="control has-icons-left has-icons-right">
                        <input
                          class="input"
                          type="text"
                          v-model="meta.wealth_status_report_api_url"
                        />
                        <span class="icon is-small is-left">
                          <i class="fa fa-server"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:tab7>
        <div class="content">
          <h2 class="title is-5">{{ $t("debt level data source") }}</h2>
          <div class="columns mt-5">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("server") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.debt_level_datasource_server"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-server"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("database") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.debt_level_datasource_database"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-database"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("username") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="text"
                    v-model="meta.debt_level_datasource_username"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-user"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">{{ $t("password") }}</label>
                <div class="control has-icons-left has-icons-right">
                  <input
                    class="input"
                    type="password"
                    v-model="meta.debt_level_datasource_password"
                  />
                  <span class="icon is-small is-left">
                    <i class="fa fa-key"></i>
                  </span>
                </div>
              </div>
            </div>
            <div class="column is-3">
              <label class="label">{{ $t("test connection") }}</label>
              <div
                class="button is-rounded is-primary"
                @click="test_connection_debt_level"
              >
                <span class="icon"><i class="fa fa-bolt"></i></span>
                <span>{{ $t("test") }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </generic-tabs>

    <div class="container mt-6">
      <div class="field mt-5">
        <div class="control">
          <button class="button is-rounded is-primary" @click="save">
            <span class="icon"><i class="fa fa-save"></i></span>
            <span>{{ $t("save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  save_configuration,
  get_configuration,
  post,
  error_message,
  success_message,
} from "@/utils";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import GenericTabs from "@/components/generic_tabs/Index.vue";

export default {
  name: "FactoringSettings",
  components: { GenericDropdown, GenericTabs },
  data() {
    return {
      meta: {},
    };
  },
  methods: {
    save() {
      let v = this;
      let data = v.meta;
      v.$root.loader(true);
      save_configuration(this, data)
        .then((response) => {
          console.log("configuration saved", response);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => v.$root.loader(false));
    },
    test_connection() {
      let v = this;
      let data = {
        server: v.meta.wealth_status_datasource_server,
        database: v.meta.wealth_status_datasource_database,
        username: v.meta.wealth_status_datasource_username,
        password: v.meta.wealth_status_datasource_password,
      };
      v.$root.loader(true);
      post("/factoring/test-wealth-status-datasource", data)
        .then(() => {
          success_message(v.$t("connection succedded"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => v.$root.loader(false));
    },
    test_fe_connection() {
      let v = this;
      let data = {
        server: v.meta.electronic_signature_datasource_server,
        database: v.meta.electronic_signature_datasource_database,
        username: v.meta.electronic_signature_datasource_username,
        password: v.meta.electronic_signature_datasource_password,
      };
      v.$root.loader(true);
      post("/factoring/test-electronic-signature-datasource", data)
        .then(() => {
          success_message(v.$t("connection succedded"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => v.$root.loader(false));
    },
    test_copy_image_connection() {
      let v = this;
      let data = {
        server: v.meta.electronic_signature_copy_image_server,
        username: v.meta.electronic_signature_copy_image_username,
        password: v.meta.electronic_signature_copy_image_password,
      };
      v.$root.loader(true);
      post("/factoring/test-electronic-signature-copy-image", data)
        .then(() => {
          success_message(v.$t("connection succedded"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => v.$root.loader(false));
    },
    sirc_script_changed(value) {
      this.meta.sirc_script_directory = value.directory;
    },
    sat_script_changed(value) {
      this.meta.sat_script_directory = value.directory;
    },
    sat_file_script_changed(value) {
      this.meta.sat_file_script_directory = value.directory;
    },
    wealth_status_script_changed(value) {
      this.meta.wealth_status_script_directory = value.directory;
    },
    sat_search_script_changed(value) {
      this.meta.sat_search_script_directory = value.directory;
    },
    sirc_drive_changed(value) {
      this.meta.sirc_drive_id = value.drive_id;
    },
    sirc_profile_changed(value) {
      if (value) {
        this.meta.sirc_profile = value.profile_id;
      }
    },
    test_connection_debt_level() {
      let v = this;
      let data = {
        server: v.meta.debt_level_datasource_server,
        database: v.meta.debt_level_datasource_database,
        username: v.meta.debt_level_datasource_username,
        password: v.meta.debt_level_datasource_password,
      };
      v.$root.loader(true);
      post("/factoring/test-debt-level-datasource", data)
        .then(() => {
          success_message(v.$t("connection succedded"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => v.$root.loader(false));
    },
  },
  created() {
    let v = this;
    v.$root.loader(true);
    get_configuration(this)
      .then((conf) => {
        if (conf) {
          v.meta = conf;
          // if (conf.sirc_script_directory)
          //   v.$refs.sirc_script_dropdown.set_value(conf.sirc_script_directory);
          if (conf.sirc_drive_id && v.$refs.sirc_drive_dropdown)
            v.$refs.sirc_drive_dropdown.set_value(conf.sirc_drive_id);
          if (conf.sat_script_directory && v.$refs.sat_script_dropdown)
            v.$refs.sat_script_dropdown.set_value(conf.sat_script_directory);
          if (
            conf.wealth_status_script_directory &&
            v.$refs.wealth_status_script_dropdown
          )
            v.$refs.wealth_status_script_dropdown.set_value(
              conf.wealth_status_script_directory
            );
          if (
            conf.sat_search_script_directory &&
            v.$refs.sat_search_script_dropdown
          )
            v.$refs.sat_search_script_dropdown.set_value(
              conf.sat_search_script_directory
            );
          if (
            conf.sat_file_script_directory &&
            v.$refs.sat_file_script_dropdown
          )
            v.$refs.sat_file_script_dropdown.set_value(
              conf.sat_file_script_directory
            );

          setTimeout(() => {
            if (conf.sirc_users_api_url && v.$refs.sirc_profile_dropdown) {
              v.$refs.sirc_profile_dropdown.init();

              setTimeout(() => {
                if (conf.sirc_profile && v.$refs.sirc_profile_dropdown)
                  v.$refs.sirc_profile_dropdown.set_value(conf.sirc_profile);
              }, 1000);
            }
          }, 1000);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => v.$root.loader(false));
  },
};
</script>

<style></style>
